import React, { useState, useEffect, useCallback } from 'react';
import { Upload, message, Icon, Progress, Select, Button, Card, Input, Tag, Divider, Alert, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { getAllTeams } from '../../utils/services/teamsAPI';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { initializeCache, getCacheStatus, isCacheInitialized, clearCache } from '../../utils/xmlDocumentImportCache';
import { importXMLDocuments } from '../../utils/xmlDocumentImportService';
import { BulkUploadButton } from '../TemplateSection/styles';
import ManageTemplatesModal from './ManageTemplatesModal';
import './styles.css';

const { Option } = Select;

const NAMESPACES = ['XML_DOCUMENT_IMPORT'];

const XMLDocumentImportPage = () => {
  const { t } = useTranslation(NAMESPACES);
  const { userDetails } = React.useContext(AppStoreContext);
  
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [templateIds, setTemplateIds] = useState({
    fr: '',
    nl: '',
    en: '',
    de: '',
  });
  const [cacheStatus, setCacheStatus] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [useReferencedBlocks, setUseReferencedBlocks] = useState(true);
  const [manageModalVisible, setManageModalVisible] = useState(false);

  // Fetch teams and categories on mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getAllTeams();
        if (response?.data) {
          setTeams(response.data);
          if (response.data.length === 1) {
            setSelectedTeamId(response.data[0].rgId);
          }
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        message.error('Failed to load teams');
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await getAllCategories(false, 'template');
        if (response?.statusCode === 507 && response?.data) {
          setCategories(response.data);
          if (response.data.length === 1) {
            setSelectedCategoryId(response.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        message.error('Failed to load categories');
      }
    };
    
    fetchTeams();
    fetchCategories();
    
    // Check cache status
    updateCacheStatus();
  }, []);

  const updateCacheStatus = () => {
    const status = getCacheStatus();
    setCacheStatus(status);
  };

  // Handle cache initialization
  const handleInitializeCache = useCallback(async () => {
    // Check if at least one template ID is provided
    const providedLanguages = Object.entries(templateIds)
      .filter(([lang, id]) => id && id.trim() !== '')
      .map(([lang]) => lang.toUpperCase());
    
    if (providedLanguages.length === 0) {
      message.error('Please provide at least one template ID');
      return;
    }

    setInitializing(true);
    setCacheStatus(null);

    try {
      const onProgress = (progressData) => {
        setCacheStatus({
          ...getCacheStatus(),
          initializing: true,
          progress: progressData,
        });
      };

      await initializeCache(templateIds, onProgress);
      updateCacheStatus();
      const availableLanguages = getCacheStatus().availableLanguages || [];
      message.success(`Cache initialized successfully for ${availableLanguages.length} language(s): ${availableLanguages.map(l => l.toUpperCase()).join(', ')}`);
    } catch (error) {
      console.error('Cache initialization error:', error);
      message.error(error.message || 'Failed to initialize cache');
      updateCacheStatus();
    } finally {
      setInitializing(false);
    }
  }, [templateIds]);

  // Handle file upload
  const handleFileUpload = useCallback(async (file) => {
    if (!selectedTeamId) {
      message.error('Please select a team first');
      return false;
    }

    if (!selectedCategoryId) {
      message.error('Please select a category first');
      return false;
    }

    if (useReferencedBlocks) {
      if (!isCacheInitialized()) {
        message.error('Please initialize cache before importing documents with referenced blocks');
        return false;
      }
      const cacheStatus = getCacheStatus();
      if (!cacheStatus.templateCount || cacheStatus.templateCount === 0) {
        message.error('No templates available. Please initialize cache with at least one template ID.');
        return false;
      }
    }

    if (!file.name.endsWith('.xml')) {
      message.error('Please upload an XML file');
      return false;
    }

    setLoading(true);
    setIsImporting(true);
    setImportProgress(null);
    setImportResult(null);

    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      // Progress callback
      const onProgress = (progressData) => {
        setImportProgress(progressData);
      };

      // Import documents
      const result = await importXMLDocuments(fileContent, selectedTeamId, selectedCategoryId, onProgress, useReferencedBlocks);
      
      setImportResult(result);
      setIsImporting(false);
      setLoading(false);

      const totalSuccess = result.successfulImports;
      const totalFailed = result.failedImports;
      
      if (totalFailed === 0) {
        message.success(`Successfully imported ${totalSuccess} document(s)`);
      } else {
        message.warning(
          `Import completed: ${totalSuccess} successful, ${totalFailed} failed`
        );
      }
    } catch (error) {
      console.error('Import error:', error);
      message.error(error.message || 'Failed to import XML file');
      setIsImporting(false);
      setLoading(false);
    }

    return false; // Prevent default upload behavior
  }, [selectedTeamId, selectedCategoryId, useReferencedBlocks]);

  const handleClearCache = () => {
    clearCache();
    updateCacheStatus();
    message.info('Cache cleared');
  };

  const progressPercent = importProgress?.totalDocuments
    ? Math.round((importProgress.processedDocuments / importProgress.totalDocuments) * 100)
    : 0;

  return (
    <div className="xml-document-import-page">
      <Card
        title="XML Document Import"
        style={{ marginBottom: '20px' }}
        extra={
          <Button
            type="default"
            icon="delete"
            onClick={() => setManageModalVisible(true)}
            disabled={isImporting || initializing}
          >
            Manage Templates
          </Button>
        }
      >
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Team:
          </label>
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Select a team"
            value={selectedTeamId}
            onChange={setSelectedTeamId}
            disabled={isImporting || initializing}
          >
            {teams.map((team) => (
              <Option key={team.rgId || team.id} value={team.rgId || team.id}>
                {team.groupName || team.name || `Team ${team.rgId || team.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Category:
          </label>
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Select a category"
            value={selectedCategoryId}
            onChange={setSelectedCategoryId}
            disabled={isImporting || initializing}
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name || `Category ${category.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <Divider orientation="left">Template Configuration (Optional)</Divider>
        
        <Alert
          message="Template IDs are optional"
          description="Provide template communicationIds only for the languages you want to import. Documents will only be created for languages with provided template IDs."
          type="info"
          showIcon
          style={{ marginBottom: '20px' }}
        />
        
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>French (FR):</label>
            <Input
              placeholder="Optional - Enter template communicationId for French"
              value={templateIds.fr}
              onChange={(e) => setTemplateIds({ ...templateIds, fr: e.target.value })}
              disabled={isImporting || initializing || isCacheInitialized()}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>Dutch (NL):</label>
            <Input
              placeholder="Optional - Enter template communicationId for Dutch"
              value={templateIds.nl}
              onChange={(e) => setTemplateIds({ ...templateIds, nl: e.target.value })}
              disabled={isImporting || initializing || isCacheInitialized()}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>English (EN):</label>
            <Input
              placeholder="Optional - Enter template communicationId for English"
              value={templateIds.en}
              onChange={(e) => setTemplateIds({ ...templateIds, en: e.target.value })}
              disabled={isImporting || initializing || isCacheInitialized()}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>German (DE):</label>
            <Input
              placeholder="Optional - Enter template communicationId for German"
              value={templateIds.de}
              onChange={(e) => setTemplateIds({ ...templateIds, de: e.target.value })}
              disabled={isImporting || initializing || isCacheInitialized()}
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <Button
            type="primary"
            onClick={handleInitializeCache}
            loading={initializing}
            disabled={isCacheInitialized() || isImporting}
          >
            {initializing ? 'Initializing Cache...' : 'Initialize Cache'}
          </Button>
          {isCacheInitialized() && (
            <Button onClick={handleClearCache} disabled={isImporting || initializing}>
              Clear Cache
            </Button>
          )}
        </div>

        {cacheStatus && (
          <Alert
            message={
              <div>
                <strong>Cache Status:</strong>
                <div style={{ marginTop: '8px' }}>
                  {cacheStatus.isInitialized ? (
                    <>
                      <Tag color="green">Initialized</Tag>
                      <span style={{ marginLeft: '10px' }}>
                        Templates: {cacheStatus.templateCount} | 
                        Block Index: {cacheStatus.blockIndexCount} entries
                      </span>
                      {cacheStatus.availableLanguages && cacheStatus.availableLanguages.length > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                          Available languages: {cacheStatus.availableLanguages.map(l => l.toUpperCase()).join(', ')}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Tag color="orange">Not Initialized</Tag>
                      {cacheStatus.initializationError && (
                        <span style={{ marginLeft: '10px', color: '#ff4d4f' }}>
                          Error: {cacheStatus.initializationError}
                        </span>
                      )}
                    </>
                  )}
                </div>
                {cacheStatus.progress && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    {cacheStatus.progress.message || cacheStatus.progress.stage}
                    {cacheStatus.progress.templateCount !== undefined && (
                      <> - Templates: {cacheStatus.progress.templateCount}</>
                    )}
                    {cacheStatus.progress.blockCount !== undefined && (
                      <> - Blocks: {cacheStatus.progress.blockCount}</>
                    )}
                  </div>
                )}
              </div>
            }
            type={cacheStatus.isInitialized ? 'success' : 'warning'}
            style={{ marginBottom: '20px' }}
          />
        )}

        <Divider orientation="left">Document Import</Divider>

        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <strong>Use Referenced Blocks</strong>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {useReferencedBlocks 
                  ? 'Blocks with iytxtbs values will be looked up from the cache and added as referenced blocks'
                  : 'All blocks will be created as local HTML blocks with default content, ignoring iytxtbs values'}
              </div>
            </div>
            <Switch
              checked={useReferencedBlocks}
              onChange={setUseReferencedBlocks}
              checkedChildren="Referenced"
              unCheckedChildren="Local"
              disabled={isImporting}
            />
          </div>
          {!useReferencedBlocks && (
            <Alert
              message="Local HTML Blocks Mode"
              description="When disabled, all blocks will be created as local HTML blocks with content from the XML. The cache initialization is not required in this mode."
              type="info"
              showIcon
              style={{ marginTop: '10px' }}
            />
          )}
        </div>

        <Upload
          accept=".xml"
          showUploadList={false}
          beforeUpload={handleFileUpload}
          disabled={!selectedTeamId || !selectedCategoryId || isImporting || (useReferencedBlocks && (!isCacheInitialized() || (getCacheStatus().templateCount || 0) === 0))}
        >
          <BulkUploadButton style={{ margin: 'auto', width: '50%' }}>
            <p className={loading ? 'downloadZip' : ''} style={{ visibility: loading ? 'visible' : 'hidden' }}>
              <Icon type="loading" style={{ marginRight: '5px', fontSize: '20px', color: '#FC6E51' }} />
            </p>
            <p className="import">
              {isImporting ? 'Importing...' : 'Upload XML File'}
            </p>
            <p className="desc">
              {isImporting
                ? 'Please wait while documents are being imported'
                : !selectedTeamId || !selectedCategoryId
                ? 'Please select a team and category before importing'
                : useReferencedBlocks && (!isCacheInitialized() || (getCacheStatus().templateCount || 0) === 0)
                ? 'Please initialize cache with at least one template before importing'
                : useReferencedBlocks && isCacheInitialized() && (getCacheStatus().availableLanguages || []).length > 0
                ? `Select an XML file to import (will import for: ${(getCacheStatus().availableLanguages || []).map(l => l.toUpperCase()).join(', ')})`
                : !useReferencedBlocks
                ? 'Select an XML file to import document definitions (local HTML blocks mode)'
                : 'Select an XML file to import document definitions'}
            </p>
          </BulkUploadButton>
        </Upload>

        {importProgress && (
          <div style={{ marginTop: '30px' }}>
            <h3>Import Progress</h3>
            <Progress
              percent={progressPercent}
              status={importProgress.stage === 'error' ? 'exception' : 'active'}
              format={(percent) => `${percent}%`}
            />
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              {importProgress.message || (
                <>
                  Processing document {importProgress.documentIndex || 0} of {importProgress.totalDocuments || 0}
                  {importProgress.language && (
                    <> - Language: {importProgress.language.toUpperCase()}</>
                  )}
                  {importProgress.documentName && (
                    <> - {importProgress.documentName}</>
                  )}
                </>
              )}
            </div>
            {importProgress.totalDocuments && (
              <div style={{ marginTop: '10px' }}>
                <Tag color="green">Successful: {importProgress.successfulImports || 0}</Tag>
                <Tag color="red">Failed: {importProgress.failedImports || 0}</Tag>
                <Tag>Total: {importProgress.totalDocuments}</Tag>
              </div>
            )}
          </div>
        )}

        {importResult && !isImporting && (
          <div style={{ marginTop: '30px' }}>
            <h3>Import Summary</h3>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <strong>Total Documents:</strong> {importResult.totalDocuments}
                </div>
                <div>
                  <strong>Processed:</strong> {importResult.processedDocuments}
                </div>
                <div>
                  <strong>Successful:</strong> <Tag color="green">{importResult.successfulImports}</Tag>
                </div>
                <div>
                  <strong>Failed:</strong> <Tag color="red">{importResult.failedImports}</Tag>
                </div>
                
                {importResult.documentResults && importResult.documentResults.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <strong>Document Details:</strong>
                    {importResult.documentResults.map((docResult, index) => (
                      <Card key={index} size="small" style={{ marginTop: '10px' }}>
                        <div>
                          <strong>{docResult.documentName}</strong>
                        </div>
                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          {Object.entries(docResult.languages).map(([lang, result]) => (
                            <div key={lang} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                              <Tag>{lang.toUpperCase()}</Tag>
                              {result.success ? (
                                <>
                                  <Tag color="green">Success</Tag>
                                  <span>CommunicationId: {result.communicationId}</span>
                                  {result.publishSuccess === true && (
                                    <Tag color="green">Published</Tag>
                                  )}
                                  {result.publishSuccess === false && (
                                    <>
                                      <Tag color="red">Publish Failed</Tag>
                                      {result.publishError && (
                                        <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                                          ({result.publishStage}: {result.publishError})
                                        </span>
                                      )}
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Tag color="red">Failed</Tag>
                                  <span style={{ color: '#ff4d4f' }}>{result.error}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                        {docResult.errors.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <strong>Errors:</strong>
                            <ul style={{ marginTop: '5px' }}>
                              {docResult.errors.map((error, errIndex) => (
                                <li key={errIndex}>
                                  {error.language}: {error.error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
                
                {importResult.errors.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <strong>General Errors:</strong>
                    <ul style={{ marginTop: '5px' }}>
                      {importResult.errors.map((error, index) => (
                        <li key={index}>
                          {error.document} ({error.language}): {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </Card>

      <ManageTemplatesModal
        visible={manageModalVisible}
        onClose={() => setManageModalVisible(false)}
        categories={categories}
      />
    </div>
  );
};

export default XMLDocumentImportPage;
